<template>
  <div class="traffic-list-v2">
    <div class="d-flex flex-wrap align-start justify-space-between mb-5">
      <div>
        <h1 class="text-h4 font-weight-bold mb-1">Traffic List v2</h1>
        <p class="text-body-1 grey--text text--darken-1 mb-0">
          Tendances des contenus dans les moteurs IA
        </p>
      </div>
      <div v-if="lastUpdatedAt" class="text-caption grey--text mt-2">
        Mis à jour : {{ formatDateTime(lastUpdatedAt) }}
      </div>
    </div>

    <v-card outlined class="filters-card mb-5">
      <v-card-text class="pb-2">
        <v-row align="center">
          <v-col cols="12" sm="6" lg="3">
            <div class="traffic-date-filter">
              <date-range-filter
                class="hide-frequency-toggle"
                periodType="past-and-today"
                format="date"
                :range="30"
                :initialValue="null"
                :onCreatedEmitDates="true"
                :frequencies="['daily']"
                :showBtnList="[]"
                :outlined="true"
                @valueChanged="setDates"
              />
            </div>
          </v-col>

          <v-col cols="12" sm="6" lg="3">
            <v-autocomplete
              v-model="draftFilters.siteId"
              :items="activeSites"
              :loading="filtersLoading"
              item-text="text"
              item-value="value"
              label="Site"
              clearable
              dense
              outlined
              hide-details
              @change="handleSiteChange"
            />
          </v-col>

          <v-col cols="12" sm="6" lg="3">
            <v-select
              v-model="draftFilters.botName"
              :items="botNames"
              :loading="botNamesLoading"
              label="Moteur IA"
              clearable
              dense
              outlined
              hide-details
              @focus="fetchBotNames"
            />
          </v-col>

          <v-col cols="12" sm="6" lg="3">
            <v-autocomplete
              v-model="draftFilters.contentTypes"
              :items="contentTypes"
              :loading="contentTypesLoading"
              label="Type de contenu"
              multiple
              clearable
              dense
              outlined
              hide-details
              small-chips
              deletable-chips
            />
          </v-col>
        </v-row>

        <v-row align="center">
          <v-col cols="12" md="4">
            <v-select
              v-model="draftFilters.source"
              :items="sources"
              :loading="sourcesLoading"
              label="Source"
              clearable
              dense
              outlined
              hide-details
              @focus="fetchSources"
            />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-model.trim="draftFilters.pathSearch"
              label="URL contenant"
              prepend-inner-icon="mdi-magnify"
              clearable
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3" class="d-flex justify-end">
            <v-btn
              text
              class="mr-2"
              :disabled="pageLoading"
              @click="resetFilters"
            >
              Réinitialiser
            </v-btn>
            <v-btn color="primary" :loading="pageLoading" @click="applyFilters">
              Appliquer
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-alert
      v-if="overview && overview.coverage && !hasContentTypeFilter"
      dense
      text
      type="info"
      class="coverage-alert mb-5"
    >
      Les analyses éditoriales couvrent
      <strong>{{ formatPercent(currentCoverage.rate_pct) }}</strong> des Hits
      IA. Les {{ formatNumber(currentCoverage.unidentified_hits) }} hits non
      identifiés restent disponibles dans le détail des URLs.
    </v-alert>

    <v-row class="mb-1">
      <v-col v-for="kpi in kpiCards" :key="kpi.key" cols="12" sm="6" lg="3">
        <v-card outlined class="kpi-card fill-height">
          <v-card-text class="d-flex align-center">
            <div :class="['kpi-icon', kpi.color]">
              <v-icon color="white" size="28">{{ kpi.icon }}</v-icon>
            </div>
            <div class="ml-4 min-width-0">
              <div class="text-body-2 grey--text text--darken-1">
                {{ kpi.label }}
              </div>
              <div class="text-h5 font-weight-bold text-truncate">
                {{ kpi.value }}
              </div>
              <div
                v-if="kpi.evolution"
                :class="['text-caption', evolutionClass(kpi.evolution.status)]"
              >
                {{ evolutionLabel(kpi.evolution) }}
                <span class="grey--text">vs période précédente</span>
              </div>
              <div v-else-if="kpi.caption" class="text-caption grey--text">
                {{ kpi.caption }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" lg="8">
        <v-card outlined class="chart-card fill-height">
          <v-card-title class="d-flex flex-wrap justify-space-between">
            <span class="text-h6 font-weight-bold">
              Évolution des Hits IA par type de contenu
            </span>
            <span v-if="periodLabel" class="text-caption grey--text">
              {{ periodLabel }}
            </span>
          </v-card-title>
          <v-card-text>
            <div v-if="overviewLoading" class="chart-loading">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <vue-apex-charts
              v-else-if="chartSeries.length"
              :key="chartRenderKey"
              type="area"
              height="360"
              :options="chartOptions"
              :series="chartSeries"
            />
            <div v-else class="empty-state">
              Aucune typologie ADN exploitable pour les filtres sélectionnés.
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="4">
        <v-card outlined class="chart-card fill-height">
          <v-card-title class="text-h6 font-weight-bold">
            Volume de Hits IA par LLM
          </v-card-title>
          <v-card-text>
            <div v-if="overviewLoading" class="chart-loading">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <vue-apex-charts
              v-else-if="llmPieSeries.length"
              :key="llmPieRenderKey"
              type="pie"
              height="360"
              :options="llmPieOptions"
              :series="llmPieSeries"
            />
            <div v-else class="empty-state">
              Aucun volume par LLM pour les filtres sélectionnés.
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="text-h6 font-weight-bold">
            Évolutions par typologie
          </v-card-title>
          <v-card-text>
            <div v-if="overviewLoading" class="chart-loading compact">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else-if="topContentTrends.length" class="trend-grid">
              <div
                v-for="trend in topContentTrends"
                :key="trend.content_type"
                class="trend-card d-flex align-center pa-3"
              >
                <div
                  :class="[
                    'trend-icon d-flex align-center justify-center',
                    trend.evolution_status === 'down'
                      ? 'trend-icon--down'
                      : 'trend-icon--up',
                  ]"
                >
                  <v-icon
                    :color="
                      trend.evolution_status === 'down' ? 'error' : 'success'
                    "
                  >
                    {{
                      trend.evolution_status === "down"
                        ? icons.mdiTrendingDown
                        : icons.mdiTrendingUp
                    }}
                  </v-icon>
                </div>
                <div class="ml-3 min-width-0 flex-grow-1">
                  <div class="font-weight-medium text-truncate">
                    {{ trend.content_type }}
                  </div>
                  <div
                    :class="[
                      'text-h6 font-weight-bold',
                      evolutionClass(trend.evolution_status),
                    ]"
                  >
                    {{ evolutionLabel(trendComparison(trend)) }}
                  </div>
                  <div class="text-caption grey--text">
                    {{ formatNumber(trend.current) }} Hits IA ·
                    {{ formatNumber(trend.urls_current) }} URLs
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="empty-state compact">
              Aucune évolution éditoriale disponible.
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="mt-5 emerging-content-card">
      <v-card-title class="text-h6 font-weight-bold">
        Contenus qui émergent
      </v-card-title>
      <v-card-text class="pt-0">
        <v-data-table
          locale="fr-FR"
          :headers="emergingContentHeaders"
          :items="emergingContent"
          :loading="emergingContentLoading"
          hide-default-footer
          disable-pagination
          class="emerging-content-table"
        >
          <template v-slot:item.content_type="{ item }">
            <v-chip
              small
              label
              class="content-type-chip"
              :style="contentTypeChipStyle(item.content_type)"
            >
              {{ item.content_type }}
            </v-chip>
          </template>

          <template v-slot:item.bot_name="{ item }">
            <div class="d-flex align-center">
              <v-icon size="20" class="mr-2">
                {{ icons.mdiRobotOutline }}
              </v-icon>
              {{ item.bot_name || "Moteur inconnu" }}
            </div>
          </template>

          <template v-slot:item.current="{ item }">
            {{ formatNumber(item.current) }}
          </template>

          <template v-slot:item.evolution_pct="{ item }">
            <span :class="evolutionClass(item.evolution_status)">
              {{ evolutionLabel(itemComparison(item)) }}
            </span>
          </template>

          <template v-slot:item.urls_current="{ item }">
            {{ formatNumber(item.urls_current) }}
          </template>

          <template v-slot:no-data>
            Aucune typologie ADN exploitable pour les filtres sélectionnés.
          </template>
        </v-data-table>

        <v-btn
          text
          color="primary"
          class="mt-3 px-0"
          @click="urlDetailsExpanded = !urlDetailsExpanded"
        >
          {{ urlDetailsExpanded ? "Masquer les URLs" : "Voir toutes les URLs" }}
          <span class="ml-2">{{ urlDetailsExpanded ? "↑" : "→" }}</span>
        </v-btn>
      </v-card-text>
    </v-card>

    <v-expand-transition>
      <v-card v-show="urlDetailsExpanded" outlined class="mt-5">
        <v-card-title class="d-flex flex-wrap align-center">
          <div>
            <div class="text-h6 font-weight-bold">Toutes les URLs</div>
            <div class="text-caption grey--text">
              Sans filtre de typologie, 100 % du trafic reste consultable, y
              compris les URLs non identifiées.
            </div>
            <div v-if="urlBotColumns.length" class="text-caption primary--text">
              {{ urlBotColumns.length }} moteurs affichés — faites défiler le
              tableau horizontalement pour voir toutes les colonnes.
            </div>
          </div>
          <v-spacer />
          <v-btn
            outlined
            color="primary"
            :loading="exportLoading"
            @click="exportExcel"
          >
            <v-icon left>{{ icons.mdiDownload }}</v-icon>
            Exporter
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-0">
          <v-data-table
            locale="fr-FR"
            :headers="urlHeaders"
            :items="urlTableItems"
            :options.sync="pagination.options"
            :server-items-length="urlData.total"
            :loading="urlsLoading"
            :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
            class="traffic-url-table"
          >
            <template v-slot:item.site_name="{ item }">
              {{ item.site_name || `Site #${item.site_id}` }}
            </template>

            <template v-slot:item.url="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <a
                    v-if="item.url"
                    :href="item.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="url-cell d-block text-truncate"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ item.path || item.url }}
                  </a>
                  <span v-else>-</span>
                </template>
                <span>{{ item.url }}</span>
              </v-tooltip>
            </template>

            <template v-slot:item.content_type="{ item }">
              <v-chip
                v-if="item.content_type"
                small
                label
                class="content-type-chip"
                :style="contentTypeChipStyle(item.content_type)"
              >
                {{ item.content_type }}
              </v-chip>
              <v-chip v-else small label outlined>Non identifié</v-chip>
            </template>

            <template v-slot:item.current="{ item }">
              {{ formatNumber(item.current) }}
            </template>

            <template v-slot:item.previous="{ item }">
              {{ formatNumber(item.previous) }}
            </template>

            <template v-slot:item.evolution_pct="{ item }">
              <span :class="evolutionClass(item.evolution_status)">
                {{ evolutionLabel(itemComparison(item)) }}
              </span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-expand-transition>

    <rmr-confirm-dialog
      v-model="slowQueryDialogVisible"
      title="Requête potentiellement lente"
      message="Aucun site n'est sélectionné. Une analyse globale sur une longue période peut prendre plusieurs minutes. Voulez-vous continuer ?"
      confirm-label="Continuer"
      cancel-label="Annuler"
      @confirm="resolveSlowQueryDialog(true)"
      @cancel="resolveSlowQueryDialog(false)"
    />
  </div>
</template>

<script>
import {
  mdiChartAreaspline,
  mdiDownload,
  mdiFileDocumentMultipleOutline,
  mdiRobotOutline,
  mdiShapeOutline,
  mdiTrendingDown,
  mdiTrendingUp,
} from "@mdi/js";
import axios from "@axios";
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "@vue/composition-api";
import VueApexCharts from "vue-apexcharts";

import DateRangeFilter from "@/components/common/filters/DateRangeFilter.vue";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import store from "@/store";
import { NOTIF_DISPLAY_DURATION } from "@/utils/constants";
import { saveRmraView } from "@/utils/rmr-analytics";

const DEFAULT_SITE_KEYWORD = "marmiton";
const CHART_COLORS = [
  "#5B4BC4",
  "#2874D0",
  "#1B9AAA",
  "#F5A623",
  "#E45B85",
  "#5A9F68",
  "#8C6BB1",
  "#D2694E",
];
const LLM_CHART_COLORS = [
  "#5B4BC4",
  "#2874D0",
  "#1B9AAA",
  "#F5A623",
  "#E45B85",
  "#5A9F68",
  "#8C6BB1",
  "#D2694E",
  "#64748B",
];

const llmNameFromBot = (botName) => {
  const normalized = String(botName || "").toLowerCase();
  if (
    normalized.includes("openai") ||
    normalized.includes("chatgpt") ||
    normalized.includes("gptbot")
  ) {
    return "OpenAI / ChatGPT";
  }
  if (normalized.includes("perplexity")) return "Perplexity";
  if (normalized.includes("claude") || normalized.includes("anthropic")) {
    return "Anthropic / Claude";
  }
  if (normalized.includes("gemini") || normalized.includes("google")) {
    return "Google / Gemini";
  }
  if (normalized.includes("mistral")) return "Mistral";
  if (normalized.includes("copilot") || normalized.includes("bing")) {
    return "Microsoft Copilot";
  }
  if (normalized.includes("meta")) return "Meta AI";
  return botName || "LLM inconnu";
};

const emptyFilters = () => ({
  siteId: null,
  botName: null,
  source: null,
  contentTypes: [],
  pathSearch: "",
  dates: null,
});

export default {
  name: "GeoTrafficListV2",
  components: {
    DateRangeFilter,
    RmrConfirmDialog,
    VueApexCharts,
  },
  setup() {
    const instance = getCurrentInstance();
    const route = instance?.proxy?.$route;
    const draftFilters = reactive(emptyFilters());
    const appliedFilters = reactive(emptyFilters());
    const overview = ref(null);
    const emergingContent = ref([]);
    const urlData = reactive({ items: [], total: 0 });
    const activeSites = ref([]);
    const botNames = ref([]);
    const sources = ref([]);
    const contentTypes = ref([]);
    const filtersLoading = ref(false);
    const botNamesLoading = ref(false);
    const sourcesLoading = ref(false);
    const botNamesLoaded = ref(false);
    const sourcesLoaded = ref(false);
    const contentTypesLoading = ref(false);
    const overviewLoading = ref(false);
    const emergingContentLoading = ref(false);
    const urlsLoading = ref(false);
    const exportLoading = ref(false);
    const lastUpdatedAt = ref(null);
    const paginationReady = ref(false);
    const urlDetailsExpanded = ref(false);
    const slowQueryDialogVisible = ref(false);
    const slowQueryDialogResolver = ref(null);
    const pagination = reactive({
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["current"],
        sortDesc: [true],
      },
    });

    const icons = {
      mdiChartAreaspline,
      mdiDownload,
      mdiFileDocumentMultipleOutline,
      mdiRobotOutline,
      mdiShapeOutline,
      mdiTrendingDown,
      mdiTrendingUp,
    };

    const pageLoading = computed(
      () =>
        overviewLoading.value ||
        emergingContentLoading.value ||
        urlsLoading.value
    );
    const currentCoverage = computed(
      () =>
        overview.value?.coverage?.current || {
          total_hits: 0,
          identified_hits: 0,
          unidentified_hits: 0,
          rate_pct: 0,
        }
    );
    const hasContentTypeFilter = computed(
      () => appliedFilters.contentTypes.length > 0
    );

    const formatNumber = (value) =>
      new Intl.NumberFormat("fr-FR").format(Number(value) || 0);
    const formatPercent = (value) =>
      `${new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Number(value) || 0)} %`;
    const formatDateTime = (value) =>
      new Intl.DateTimeFormat("fr-FR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(value);

    const evolutionClass = (status) => {
      if (status === "up" || status === "new") return "success--text";
      if (status === "down") return "error--text";
      return "grey--text";
    };
    const evolutionLabel = (item) => {
      if (!item) return "";
      if (item.status === "new") return "Nouveau";
      const value = Number(item.value) || 0;
      const prefix = value > 0 ? "+" : "";
      return `${prefix}${formatPercent(value)}`;
    };
    const trendComparison = (item) => ({
      status: item.evolution_status,
      value: item.evolution_pct,
    });
    const itemComparison = trendComparison;

    const kpiCards = computed(() => {
      const value = overview.value;
      const hits = value?.hits_ia || {};
      const urls = value?.unique_urls || {};
      const types = (value?.content_types || []).filter(
        (item) => Number(item.current) > 0
      );
      return [
        {
          key: "hits",
          label: "Hits IA",
          value: formatNumber(hits.current),
          evolution: value
            ? {
                status: hits.evolution_status,
                value: hits.evolution_pct,
              }
            : null,
          icon: icons.mdiRobotOutline,
          color: "indigo",
        },
        {
          key: "urls",
          label: "URLs détectées",
          value: formatNumber(urls.current),
          evolution: value
            ? {
                status: urls.evolution_status,
                value: urls.evolution_pct,
              }
            : null,
          icon: icons.mdiFileDocumentMultipleOutline,
          color: "blue",
        },
        {
          key: "types",
          label: "Typologies couvertes",
          value: formatNumber(types.length),
          caption: "Valeurs ADN actives sur la période",
          icon: icons.mdiShapeOutline,
          color: "teal",
        },
        {
          key: "coverage",
          label: "Couverture ADN",
          value: formatPercent(currentCoverage.value.rate_pct),
          caption: hasContentTypeFilter.value
            ? "Périmètre limité aux typologies sélectionnées"
            : `${formatNumber(
                currentCoverage.value.identified_hits
              )} Hits IA identifiés`,
          icon: icons.mdiChartAreaspline,
          color: "orange",
        },
      ];
    });

    const periodLabel = computed(() => {
      const period = overview.value?.period;
      if (!period) return "";
      return `${period.date_from} → ${period.date_to}`;
    });

    const chartDates = computed(() => [
      ...new Set((overview.value?.daily_series || []).map((item) => item.date)),
    ]);
    const chartContentTypes = computed(() => {
      const totals = (overview.value?.daily_series || []).reduce(
        (acc, item) => {
          acc[item.content_type] =
            (acc[item.content_type] || 0) + Number(item.hits);
          return acc;
        },
        {}
      );
      return Object.entries(totals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)
        .map(([name]) => name);
    });
    const chartSeries = computed(() =>
      chartContentTypes.value.map((contentType) => ({
        name: contentType,
        data: chartDates.value.map((date) => {
          const point = (overview.value?.daily_series || []).find(
            (item) => item.date === date && item.content_type === contentType
          );
          return Number(point?.hits) || 0;
        }),
      }))
    );
    const chartRenderKey = computed(
      () => `${chartDates.value.join("|")}:${chartContentTypes.value.join("|")}`
    );
    const formatChartDate = (value) => {
      if (!value) return "";
      const normalizedValue = String(value).slice(0, 10);
      const date = new Date(`${normalizedValue}T00:00:00`);
      if (Number.isNaN(date.getTime())) return normalizedValue;
      return new Intl.DateTimeFormat("fr-FR", {
        day: "numeric",
        month: "short",
      }).format(date);
    };
    const chartOptions = computed(() => ({
      chart: {
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: "inherit",
      },
      colors: CHART_COLORS,
      dataLabels: { enabled: false },
      fill: {
        type: "gradient",
        gradient: { opacityFrom: 0.75, opacityTo: 0.28 },
      },
      grid: { borderColor: "rgba(148, 163, 184, 0.2)", strokeDashArray: 4 },
      legend: { position: "top", horizontalAlign: "right" },
      stroke: { curve: "smooth", width: 2 },
      tooltip: {
        shared: true,
        y: { formatter: (value) => `${formatNumber(value)} Hits IA` },
      },
      xaxis: {
        categories: chartDates.value,
        labels: {
          formatter: formatChartDate,
        },
      },
      yaxis: { labels: { formatter: (value) => formatNumber(value) } },
    }));

    const llmVolumes = computed(() => {
      const totals = (overview.value?.bots || []).reduce((acc, item) => {
        const llmName = llmNameFromBot(item.bot_name);
        acc[llmName] = (acc[llmName] || 0) + Number(item.current || 0);
        return acc;
      }, {});
      return Object.entries(totals)
        .filter(([, hits]) => hits > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([name, hits]) => ({ name, hits }));
    });
    const llmPieSeries = computed(() =>
      llmVolumes.value.map((item) => item.hits)
    );
    const llmPieRenderKey = computed(() =>
      llmVolumes.value.map((item) => `${item.name}:${item.hits}`).join("|")
    );
    const llmPieOptions = computed(() => ({
      chart: {
        toolbar: { show: false },
        fontFamily: "inherit",
      },
      colors: LLM_CHART_COLORS,
      labels: llmVolumes.value.map((item) => item.name),
      dataLabels: {
        enabled: true,
        formatter: (value) => (Number(value) >= 3 ? formatPercent(value) : ""),
      },
      legend: {
        position: "bottom",
        fontSize: "13px",
      },
      stroke: { colors: ["#ffffff"], width: 2 },
      tooltip: {
        y: { formatter: (value) => `${formatNumber(value)} Hits IA` },
      },
      responsive: [
        {
          breakpoint: 1264,
          options: {
            chart: { height: 320 },
          },
        },
      ],
    }));

    const topContentTrends = computed(() =>
      [...(overview.value?.content_types || [])]
        .filter((item) => item.evolution_status !== "stable")
        .sort((a, b) => {
          if (a.evolution_status === "new" && b.evolution_status === "new") {
            return Number(b.current) - Number(a.current);
          }
          if (a.evolution_status === "new") return -1;
          if (b.evolution_status === "new") return 1;
          return (
            Math.abs(Number(b.evolution_pct) || 0) -
            Math.abs(Number(a.evolution_pct) || 0)
          );
        })
        .slice(0, 5)
    );

    const emergingContentHeaders = [
      { text: "Type", value: "content_type", sortable: false },
      { text: "Moteur", value: "bot_name", sortable: false },
      { text: "Hits IA", value: "current", align: "end", sortable: false },
      {
        text: "Évolution",
        value: "evolution_pct",
        align: "end",
        sortable: false,
      },
      {
        text: "URLs",
        value: "urls_current",
        align: "end",
        sortable: false,
      },
    ];

    const urlBotColumns = computed(() => {
      const names = [];
      (overview.value?.bots || [])
        .filter((bot) => Number(bot.current) > 0)
        .forEach((bot) => names.push(bot.bot_name || "Moteur inconnu"));
      urlData.items.forEach((item) => {
        Object.keys(item.hits_by_bot || {}).forEach((botName) => {
          if (!names.includes(botName)) names.push(botName);
        });
      });
      return names.map((botName, index) => ({
        text: botName,
        value: `bot_hits_${index}`,
        botName,
        align: "end",
        sortable: false,
        width: 140,
      }));
    });
    const urlTableItems = computed(() =>
      urlData.items.map((item) => {
        const tableItem = { ...item };
        urlBotColumns.value.forEach((column) => {
          tableItem[column.value] = formatNumber(
            item.hits_by_bot?.[column.botName]
          );
        });
        return tableItem;
      })
    );
    const urlHeaders = computed(() => [
      { text: "Site", value: "site_name", sortable: true },
      { text: "URL", value: "url", sortable: true },
      { text: "Type de contenu", value: "content_type", sortable: true },
      {
        text: "Hits IA total",
        value: "current",
        align: "end",
        sortable: true,
      },
      ...urlBotColumns.value,
      {
        text: "Période précédente",
        value: "previous",
        align: "end",
        sortable: true,
      },
      {
        text: "Évolution",
        value: "evolution_pct",
        align: "end",
        sortable: true,
      },
    ]);

    const contentTypeColor = (value) => {
      const chartIndex = chartContentTypes.value.indexOf(value);
      if (chartIndex >= 0)
        return CHART_COLORS[chartIndex % CHART_COLORS.length];

      const distributionIndex = (overview.value?.content_types || []).findIndex(
        (item) => item.content_type === value
      );
      if (distributionIndex >= 0) {
        return CHART_COLORS[distributionIndex % CHART_COLORS.length];
      }

      const hash = String(value)
        .split("")
        .reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return CHART_COLORS[hash % CHART_COLORS.length];
    };
    const contentTypeChipStyle = (value) => {
      const backgroundColor = contentTypeColor(value);
      const [red, green, blue] = backgroundColor
        .slice(1)
        .match(/.{2}/g)
        .map((item) => Number.parseInt(item, 16));
      const perceivedBrightness = (red * 299 + green * 587 + blue * 114) / 1000;
      return {
        backgroundColor,
        color: perceivedBrightness > 155 ? "#263238" : "#ffffff",
      };
    };

    const paramsSerializer = (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => searchParams.append(key, item));
        } else if (value !== null && value !== undefined && value !== "") {
          searchParams.append(key, value);
        }
      });
      return searchParams.toString();
    };

    const buildParams = () => ({
      site_id: appliedFilters.siteId,
      bot_name: appliedFilters.botName,
      source: appliedFilters.source,
      content_type: appliedFilters.contentTypes,
      path_patterns_included: appliedFilters.pathSearch
        ? [`*${appliedFilters.pathSearch}*`]
        : null,
      dates: appliedFilters.dates,
    });

    const mapSort = (value) => {
      const map = {
        current: "current_hits",
        previous: "previous_hits",
        evolution_pct: "evolution",
        site_name: "site",
        url: "path",
      };
      return map[value] || value || "current_hits";
    };

    const notifyError = async (text) => {
      await store.dispatch("app/setSnackBar", {
        show: true,
        text,
        color: "error",
        timeout: NOTIF_DISPLAY_DURATION,
      });
    };

    const fetchOverview = async () => {
      overviewLoading.value = true;
      try {
        const response = await axios.get("/geo/traffic/v2/overview", {
          params: buildParams(),
          paramsSerializer,
        });
        overview.value = response.data;
        lastUpdatedAt.value = new Date();
      } catch (error) {
        console.error("Traffic List v2 overview error:", error);
        await notifyError(
          "Erreur lors du chargement des tendances Traffic List v2."
        );
      } finally {
        overviewLoading.value = false;
      }
    };

    const fetchEmergingContent = async () => {
      emergingContentLoading.value = true;
      try {
        const response = await axios.get("/geo/traffic/v2/emerging-content", {
          params: { ...buildParams(), limit: 5 },
          paramsSerializer,
        });
        emergingContent.value = response.data || [];
      } catch (error) {
        console.error("Traffic List v2 emerging content error:", error);
        emergingContent.value = [];
        await notifyError(
          "Erreur lors du chargement des contenus qui émergent."
        );
      } finally {
        emergingContentLoading.value = false;
      }
    };

    const fetchUrls = async () => {
      urlsLoading.value = true;
      try {
        const sortBy = pagination.options.sortBy?.[0] || "current";
        const sortDesc = pagination.options.sortDesc?.[0] !== false;
        const response = await axios.get("/geo/traffic/v2/urls", {
          params: {
            ...buildParams(),
            page: pagination.options.page - 1,
            limit: pagination.options.itemsPerPage,
            sort_by: mapSort(sortBy),
            sort_desc: sortDesc,
          },
          paramsSerializer,
        });
        urlData.items = response.data.items || [];
        urlData.total = response.data.total || 0;
      } catch (error) {
        console.error("Traffic List v2 URLs error:", error);
        await notifyError(
          "Erreur lors du chargement des URLs Traffic List v2."
        );
      } finally {
        urlsLoading.value = false;
      }
    };

    const fetchPageData = async () => {
      await Promise.all([fetchOverview(), fetchEmergingContent(), fetchUrls()]);
    };

    const fetchContentTypes = async (siteId) => {
      contentTypesLoading.value = true;
      try {
        const response = await axios.get("/geo/traffic/v2/content-types", {
          params: { site_id: siteId || null },
        });
        contentTypes.value = response.data || [];
        draftFilters.contentTypes = draftFilters.contentTypes.filter((item) =>
          contentTypes.value.includes(item)
        );
      } catch (error) {
        console.error("Traffic List v2 content types error:", error);
      } finally {
        contentTypesLoading.value = false;
      }
    };

    const fetchBotNames = async () => {
      if (botNamesLoading.value || botNamesLoaded.value) return;
      botNamesLoading.value = true;
      try {
        const response = await axios.get("/geo/traffic/bots");
        botNames.value = response.data || [];
        botNamesLoaded.value = true;
      } catch (error) {
        console.error("Traffic List v2 bot names error:", error);
      } finally {
        botNamesLoading.value = false;
      }
    };

    const fetchSources = async () => {
      if (sourcesLoading.value || sourcesLoaded.value) return;
      sourcesLoading.value = true;
      try {
        const response = await axios.get("/geo/traffic/sources");
        sources.value = response.data || [];
        sourcesLoaded.value = true;
      } catch (error) {
        console.error("Traffic List v2 sources error:", error);
      } finally {
        sourcesLoading.value = false;
      }
    };

    const fetchFilterValues = async () => {
      filtersLoading.value = true;
      try {
        const sitesResponse = await axios.get("/geo/traffic/sites");
        activeSites.value = (sitesResponse.data || [])
          .map((site) => ({
            text: site.name || site.domain,
            value: site.id,
            domain: site.domain || "",
          }))
          .sort((a, b) => a.text.localeCompare(b.text));
      } catch (error) {
        console.error("Traffic List v2 filters error:", error);
        await notifyError(
          "Erreur lors du chargement des filtres Traffic List v2."
        );
      } finally {
        filtersLoading.value = false;
      }
    };

    const setDates = (value) => {
      if (Array.isArray(value) && value.length === 2) {
        draftFilters.dates = `${value[0]},${value[1]}`;
      } else if (typeof value === "string" && value.includes(",")) {
        draftFilters.dates = value;
      } else {
        draftFilters.dates = null;
      }
    };

    const handleSiteChange = async (siteId) => {
      draftFilters.contentTypes = [];
      await fetchContentTypes(siteId);
    };

    const copyDraftToApplied = () => {
      appliedFilters.siteId = draftFilters.siteId || null;
      appliedFilters.botName = draftFilters.botName || null;
      appliedFilters.source = draftFilters.source || null;
      appliedFilters.contentTypes = [...draftFilters.contentTypes];
      appliedFilters.pathSearch = String(draftFilters.pathSearch || "").trim();
      appliedFilters.dates = draftFilters.dates || null;
    };

    const resolveSlowQueryDialog = (confirmed) => {
      slowQueryDialogVisible.value = false;
      if (slowQueryDialogResolver.value) {
        slowQueryDialogResolver.value(confirmed);
        slowQueryDialogResolver.value = null;
      }
    };

    const confirmGlobalQuery = () => {
      slowQueryDialogVisible.value = true;
      return new Promise((resolve) => {
        slowQueryDialogResolver.value = resolve;
      });
    };

    const applyFilters = async () => {
      if (!draftFilters.siteId) {
        const confirmed = await confirmGlobalQuery();
        if (!confirmed) return;
      }
      copyDraftToApplied();
      paginationReady.value = false;
      pagination.options.page = 1;
      await fetchPageData();
      paginationReady.value = true;
    };

    const resetFilters = async () => {
      const defaultSite = activeSites.value.find((site) =>
        `${site.text} ${site.domain}`
          .toLowerCase()
          .includes(DEFAULT_SITE_KEYWORD)
      );
      Object.assign(draftFilters, emptyFilters(), {
        siteId: defaultSite?.value || activeSites.value[0]?.value || null,
      });
      await fetchContentTypes(draftFilters.siteId);
      copyDraftToApplied();
      paginationReady.value = false;
      pagination.options.page = 1;
      await fetchPageData();
      paginationReady.value = true;
    };

    const exportExcel = async () => {
      exportLoading.value = true;
      try {
        const sortBy = pagination.options.sortBy?.[0] || "current";
        const sortDesc = pagination.options.sortDesc?.[0] !== false;
        const response = await axios.get("/geo/traffic/v2/export", {
          params: {
            ...buildParams(),
            sort_by: mapSort(sortBy),
            sort_desc: sortDesc,
          },
          paramsSerializer,
          responseType: "blob",
        });
        const objectUrl = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = "geo-traffic-v2-export.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error("Traffic List v2 export error:", error);
        await notifyError("Erreur lors de l’export Traffic List v2.");
      } finally {
        exportLoading.value = false;
      }
    };

    onMounted(async () => {
      store.commit("appConfig/UPDATE_APP_CONTENT_WIDTH", "fullWidth");
      await fetchFilterValues();
      const routeSiteId = Number.parseInt(route?.query?.site_id, 10);
      const defaultSite = activeSites.value.find((site) =>
        `${site.text} ${site.domain}`
          .toLowerCase()
          .includes(DEFAULT_SITE_KEYWORD)
      );
      draftFilters.siteId = Number.isNaN(routeSiteId)
        ? defaultSite?.value || activeSites.value[0]?.value || null
        : routeSiteId;
      await fetchContentTypes(draftFilters.siteId);
      copyDraftToApplied();
      await fetchPageData();
      paginationReady.value = true;
      if (route) saveRmraView(route);
    });

    onBeforeUnmount(() => {
      store.commit("appConfig/UPDATE_APP_CONTENT_WIDTH", "boxed");
      if (slowQueryDialogResolver.value) {
        slowQueryDialogResolver.value(false);
      }
    });

    watch(
      () => pagination.options,
      () => {
        if (paginationReady.value) {
          fetchUrls();
        }
      },
      { deep: true }
    );

    return {
      activeSites,
      applyFilters,
      botNames,
      botNamesLoading,
      chartOptions,
      chartRenderKey,
      chartSeries,
      contentTypeChipStyle,
      contentTypes,
      contentTypesLoading,
      currentCoverage,
      draftFilters,
      emergingContent,
      emergingContentHeaders,
      emergingContentLoading,
      evolutionClass,
      evolutionLabel,
      exportExcel,
      exportLoading,
      fetchBotNames,
      fetchSources,
      filtersLoading,
      formatDateTime,
      formatNumber,
      formatPercent,
      hasContentTypeFilter,
      handleSiteChange,
      icons,
      itemComparison,
      kpiCards,
      lastUpdatedAt,
      llmPieOptions,
      llmPieRenderKey,
      llmPieSeries,
      overview,
      overviewLoading,
      pageLoading,
      pagination,
      periodLabel,
      resetFilters,
      resolveSlowQueryDialog,
      setDates,
      slowQueryDialogVisible,
      sources,
      sourcesLoading,
      topContentTrends,
      trendComparison,
      urlData,
      urlDetailsExpanded,
      urlHeaders,
      urlBotColumns,
      urlTableItems,
      urlsLoading,
    };
  },
};
</script>

<style scoped>
.traffic-list-v2 {
  --traffic-border: rgba(148, 163, 184, 0.25);
}

.filters-card,
.kpi-card,
.chart-card,
.emerging-content-card,
.trend-card {
  border-color: var(--traffic-border) !important;
}

.filters-card,
.kpi-card,
.chart-card,
.emerging-content-card {
  border-radius: 12px;
}

.kpi-card {
  min-height: 122px;
}

.kpi-icon {
  align-items: center;
  border-radius: 12px;
  display: flex;
  flex: 0 0 58px;
  height: 58px;
  justify-content: center;
  width: 58px;
}

.trend-card {
  border: 1px solid var(--traffic-border);
  border-radius: 10px;
}

.trend-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.trend-icon {
  background: rgba(40, 167, 69, 0.1);
  border-radius: 9px;
  flex: 0 0 48px;
  height: 48px;
  width: 48px;
}

.trend-icon--down {
  background: rgba(234, 84, 85, 0.1);
}

.chart-loading,
.empty-state {
  align-items: center;
  display: flex;
  height: 360px;
  justify-content: center;
  text-align: center;
}

.chart-loading.compact,
.empty-state.compact {
  height: 180px;
}

.coverage-alert {
  border-radius: 10px;
}

.url-cell {
  max-width: 520px;
  text-decoration: none;
}

.min-width-0 {
  min-width: 0;
}

::v-deep .hide-frequency-toggle .v-btn-toggle-frequency {
  display: none !important;
}

::v-deep .traffic-date-filter .v-text-field__details {
  display: none;
}

::v-deep .traffic-url-table th {
  white-space: nowrap;
}

::v-deep .traffic-url-table .v-data-table__wrapper {
  overflow-x: auto;
}

@media (max-width: 960px) {
  .url-cell {
    max-width: 300px;
  }
}
</style>
